import { DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, Signal, signal, WritableSignal } from '@angular/core';
import { RequestTypesKeys } from '@app/enums/RequestTypes';
import {
    AgColumn,
    AgPublicEventType,
    IRowNode,
    IStatusPanelParams,
} from '@medpacesoftwaredevelopment/designsystem/lib/components/ag-grid';
import { StudyExpenseTrackingColumnId } from '@models/set-col-id';
import { StudyExpenseTrackingDataRow } from '@models/studyExpenseTrackingDataRow';
import { IStatusPanelAngularComp } from 'ag-grid-angular';
import { isNumber, uniqWith } from 'lodash-es';

type Selection = { colId: string; rowId: number; rowNode?: IRowNode<StudyExpenseTrackingDataRow> };

@Component({
    selector: 'medpace-ag-grid-status-bar',
    templateUrl: './ag-grid-status-bar.component.html',
    imports: [DecimalPipe],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AgGridStatusBarComponent implements IStatusPanelAngularComp {
    // Add column keys here to sum their values in SET (only numeric values)
    private readonly allowedColumns: StudyExpenseTrackingColumnId[] = [
        'requestedAmount',
        'amountAuthorized',
        'totalUSD',
    ];

    private readonly requiredNewExpenseColumns: StudyExpenseTrackingColumnId[] = [
        'receiptDate',
        'expenseType',
        'localCurrencyCode',
        'requestedAmount',
        'amountAuthorized',
    ];

    private readonly eventsToRegister: AgPublicEventType[] = [
        'rangeSelectionChanged',
        'rowSelected',
        'columnEverythingChanged',
        'rowDataUpdated',
        'filterChanged',
    ];

    private readonly params: WritableSignal<IStatusPanelParams<StudyExpenseTrackingDataRow> | undefined> =
        signal(undefined);

    private readonly cellValues: Signal<number[]> = computed(() => {
        const selections = this.selections();
        const gridApi = this.params()?.api;

        if (selections.length === 0 || !gridApi) return [];

        const rowIds = uniqWith(selections.filter((sel) => !sel.rowNode).map((sel) => sel.rowId));
        const rowNodes: Map<number, IRowNode<StudyExpenseTrackingDataRow>> = new Map();

        gridApi?.forEachNode((rowNode) => {
            if (rowIds.some((id) => id === rowNode.rowIndex)) rowNodes.set(rowNode.rowIndex ?? 0, rowNode);
        });

        const values = selections
            .map((sel) => {
                const rowNode = sel.rowNode || rowNodes.get(sel.rowId);
                if (!rowNode || !rowNode.displayed || !gridApi.getColumn(sel.colId)?.isVisible()) return null;
                return gridApi?.getCellValue({ rowNode: rowNode, colKey: sel.colId });
            })
            .filter(isNumber);

        return values;
    });

    protected readonly selections: WritableSignal<Selection[]> = signal([]);

    protected readonly hiddenRequiredColIds: WritableSignal<string[]> = signal([]);

    protected readonly isSumVisible: Signal<boolean> = computed(() => this.cellValues().length > 1);

    protected readonly showStatusBar: Signal<boolean> = computed(
        () => this.isSumVisible() || this.hiddenRequiredColIds().length > 0
    );

    protected readonly sum: Signal<number> = computed(() => this.cellValues().reduce((acc, curr) => acc + curr));

    public agInit(params: IStatusPanelParams<StudyExpenseTrackingDataRow>): void {
        this.params.set(params);

        this.eventsToRegister.forEach((eventType) => {
            this.params()?.api.addEventListener(eventType, this.onSelectionChanged.bind(this));
        });
    }

    private onSelectionChanged(): void {
        this.selectedCellsSumStatusBar();

        this.newExpenseHiddenColumnsStatusBar();
    }

    private selectedCellsSumStatusBar() {
        const rangeSelections = this.params()?.api.getCellRanges();

        const allSelections: Selection[] = [];

        if ((rangeSelections?.length ?? 0) > 0) {
            const allRangeSelections: Selection[] = (rangeSelections ?? [])
                ?.map((selection) => {
                    const colIds = (selection.columns as AgColumn[]).map((col) => col.colId);

                    const startRowId = Math.min(selection.startRow?.rowIndex ?? 0, selection.endRow?.rowIndex ?? 0);
                    const endRowId = Math.max(selection.startRow?.rowIndex ?? 0, selection.endRow?.rowIndex ?? 0);
                    const rowIds = Array.from({ length: endRowId - startRowId + 1 }, (_, index) => startRowId + index);

                    return colIds.flatMap((colId) => rowIds.map((rowId) => ({ colId: colId, rowId: rowId })));
                })
                .flat();

            allSelections.push(...allRangeSelections);
        }

        const rowSelections = this.params()?.api.getSelectedNodes();
        if ((rowSelections?.length ?? 0) > 0) {
            const allRowSelections: Selection[] = (rowSelections ?? [])
                ?.map((rowNode) => {
                    const colIds: string[] = this.allowedColumns;
                    return colIds.flatMap((colId) => ({
                        colId: colId,
                        rowNode: rowNode,
                        rowId: rowNode.rowIndex ?? 0,
                    }));
                })
                .flat();

            allSelections.push(...allRowSelections);
        }

        const distinctAllowedSelections = uniqWith(
            allSelections,
            (a, b) => a.colId === b.colId && a.rowId === b.rowId
        ).filter((sel) => this.allowedColumns.some((colId) => colId === sel.colId));

        this.selections.set(distinctAllowedSelections);
    }

    private newExpenseHiddenColumnsStatusBar() {
        let isNewExpense: boolean = false;
        this.params()?.api.forEachNode((rowNode) => {
            if (rowNode.data?.requestType === RequestTypesKeys.REIMBURSEMENT && rowNode.data?.expenseId === 0) {
                isNewExpense = true;
            }
        });

        if (!isNewExpense) {
            return;
        }

        const hiddenCols =
            this.params()
                ?.api.getAllGridColumns()
                ?.filter((col) => !col.isVisible())
                .map((col) => ({ colId: col.getColId(), colName: col.getColDef().headerName })) ?? [];

        const hiddenRequiredColIds = hiddenCols.filter((col) =>
            this.requiredNewExpenseColumns.includes(col.colId as StudyExpenseTrackingColumnId)
        );

        this.hiddenRequiredColIds.set(hiddenRequiredColIds.map((col) => col.colName || col.colId));
    }
}
